import CustomReactSelect from './custom-react-select';
import { reactSelectAdminStyles } from './react-select-styles';
import { components as ReactSelectComponents } from 'react-select';
import Text from '@ui/text/text/text';
import type { TReactSelectOption } from './custom-react-select.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useState, type CSSProperties } from 'react';
import Container from '@ui/container/container';

const styleMap = (width: CSSProperties['width'] = '137px') =>
  reactSelectAdminStyles({
    control: () => ({
      padding: 0,
      paddingLeft: 0,
      border: '1px solid var(--gray-500)',
      margin: 0,
      maxHeight: '30px',
      width,
    }),
    singleValue: () => ({
      color: 'var(--gray-100)',
      /* leaving these empty allows our S3 classNames to properly work because react-select will strip empty values */
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '16px',
      letterSpacing: '0.24px',
    }),
    placeholder: () => ({ fontSize: '13px', fontWeight: 500 }),
    menuPortal: () => ({ zIndex: 1100 }),
    menuList: () => ({
      minWidth: '180px',
      width,
    }),
    option: () => ({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
    }),
  });

interface Props {
  value: string;
  options: { label: string; value: string; isDisabled?: boolean }[];
  onChange: (value: string) => void;
  placeholder: string;
  forcePlaceholder?: boolean;
  prefix?: string;
  width?: CSSProperties['width'];
  menuPortal?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}
export default function SelectSingle({
  value,
  options,
  onChange,
  placeholder,
  forcePlaceholder,
  prefix,
  menuPortal = true,
  width,
  onMenuOpen,
  onMenuClose,
}: Props) {
  const [touched, setTouched] = useState(false);
  return (
    <CustomReactSelect
      value={forcePlaceholder && !touched ? undefined : options.find((opt) => opt.value === value)}
      placeholder={placeholder}
      options={options}
      isSearchable={false}
      closeMenuOnSelect
      styles={styleMap(width)}
      menuPlacement="auto"
      menuPortalTarget={menuPortal ? document.body : undefined}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      components={{
        Option: (option) => {
          const data = option.data as TReactSelectOption;
          return (
            <ReactSelectComponents.Option {...option}>
              <Text className="P2">{data.label}</Text>
              {option.isSelected && <Icon name={ICONS.CHECKMARK} size={10} />}
            </ReactSelectComponents.Option>
          );
        },
        SingleValue: (option) => {
          if (prefix) {
            return (
              <Container className="flex ai-center padding-l-8" style={{ gridArea: '1 / 1 / 2 / 3' }} gap={'8px'}>
                <Text className="P2" design="GlobalIcoText3" style={{ whiteSpace: 'nowrap' }}>
                  {prefix}:
                </Text>
                <ReactSelectComponents.SingleValue {...option} />
              </Container>
            );
          }
          return <ReactSelectComponents.SingleValue {...option} />;
        },
      }}
      onChange={(option) => {
        setTouched(true);
        onChange(String((option as TReactSelectOption).value));
      }}
    />
  );
}
